/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import TrophySVG from 'bundles/enterprise-learning-path-description-page/assets/TrophySVG';
import EnterpriseSkillPathwayCourseActionButton from 'bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayCourseActionButton';
import {
  getEnrollingCourseAssessmentActionId,
  shouldDisableActionButton,
  useSkillPathwayActivelyEnrolling,
} from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayActivelyEnrollingContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  container: css`
    margin-top: var(--cds-spacing-150);
    background-color: var(--cds-color-blue-25);
    padding: var(--cds-spacing-200) var(--cds-spacing-150);
    border-radius: 16px;
    margin-right: 0 !important;
    margin-left: 0 !important;
    margin-bottom: 0 !important;
    width: unset !important;
  `,
  boldText: css`
    font-weight: 600;
  `,
  buttonContainer: css`
    margin-top: 2px;
  `,

  lastItem: css`
    margin-left: auto;
  `,
};

type Props = {
  courseTitle: string;
  courseSlug: string;
  courseId: string;
  pathwayId: string;
  isEnrolledInPathway: boolean;
  isEnrolledInCourse: boolean;
  isProgramMember: boolean;
  pathwayCollectionId?: string;
  programId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  activateProgramAccessModal?: () => void;
  assessmentItemId?: string;
};

const SkillPathwayCourseActionsV2 = ({
  courseTitle,
  courseSlug,
  courseId,
  pathwayId,
  pathwayCollectionId,
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  programId,
  programSlug,
  thirdPartyOrganizationSlug,
  activateProgramAccessModal,
  assessmentItemId,
}: Props) => {
  const { setCurrentEnrollingAction, currentEnrollingAction } = useSkillPathwayActivelyEnrolling();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  if (!programId || !thirdPartyOrganizationSlug) {
    return null;
  }

  return (
    <Grid container css={styles.container} spacing={12}>
      {!isMobile && (
        <Grid item>
          <TrophySVG />
        </Grid>
      )}
      <Grid item>
        <div>
          <Typography2 variant="subtitleSmall" component="p">
            {_t('Already know this material?')}
          </Typography2>
          <Typography2 variant={isMobile ? 'bodyTertiary' : 'bodySecondary'} component="p">
            {_t('Take an assessment to skip ahead')}
          </Typography2>
        </div>
      </Grid>
      <Grid item css={styles.lastItem}>
        <EnterpriseSkillPathwayCourseActionButton
          isEnrolledInPathway={isEnrolledInPathway}
          isEnrolledInCourse={isEnrolledInCourse}
          isProgramMember={isProgramMember}
          programId={programId}
          courseId={courseId}
          courseSlug={courseSlug}
          pathwayCollectionId={pathwayCollectionId}
          pathwayId={pathwayId}
          programSlug={programSlug}
          thirdPartyOrganizationSlug={thirdPartyOrganizationSlug}
          activateProgramAccessModal={activateProgramAccessModal}
          isEnrollingCallback={setCurrentEnrollingAction}
          disabled={shouldDisableActionButton(getEnrollingCourseAssessmentActionId(courseId), currentEnrollingAction)}
          redirectToAssessment
        />
      </Grid>
    </Grid>
  );
};

export default SkillPathwayCourseActionsV2;
